import type { FC } from 'react';
import { Card } from '@/components/ui/card';
import type { Invoice, CompanySettings, InvoiceSettings, PaymentMethod } from '@/types/firebase';
import { formatCurrency, formatPercentage } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { useState, useEffect, useRef, useMemo } from 'react';
import { PaymentModal } from './PaymentModal';
import { ACHPaymentModal } from '@/components/ACHPaymentModal';
import { Shield, Clock } from 'lucide-react';
import { isAchPaymentAvailable } from '@/utils/paymentGateways';


interface InvoicePreviewProps {
  invoice: Partial<Invoice> & {
    // Add additional address fields that might exist on the customer object
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  companySettings: Partial<CompanySettings>;
  className?: string;
  showPreviewWatermark?: boolean;
  isPreviewAndSend?: boolean; // New prop to identify when in Preview & Send context
}

const defaultInvoiceSettings: InvoiceSettings = {
  showLogo: false,
  enablePlatformFee: false,
  industry: 'none',
  invoiceType: 'standard',
  paymentMethods: {
    credit_card: false,
    paypal: false,
    ach: false,
    check: false
  },
  invoiceStartingNumber: 1000,
  enableDatePrefix: false,
  invoicePrefix: '',
  showPhoneOnInvoice: true,
  showEmailOnInvoice: true,
  invoicePhone: '',
  invoiceEmail: '',
  colors: {
    primary: '#4F46E5',
    secondary: '#6B7280',
    headerText: '#111827',
    tableHeader: '#F3F4F6',
    tableHeaderText: '#111827',
    totalText: '#111827',
    linkText: '#FFFFFF',
    buttonBackground: '#22C55E'
  },
  template: 'professional',
  numberFormat: 'INV-{NUMBER}',
  zeroPadding: 5,
  resetNumberSequence: 'never',
  dateFormat: 'YYYY-MM-DD',
  customFooterText: '',
  defaultDueTerms: 'net_30',
  defaultNotes: '',
  showUnpaidWatermark: false,
  showPaidWatermark: false,
  showNotesOnInvoice: true,
  showTermsOnInvoice: true,
};

// Add responsive mobile styles
const mobileStyles = `
  @media (max-width: 640px) {
    /* General mobile styles */
    .invoice-card {
      width: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
      border: none !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      overflow-x: hidden !important;
    }
    
    .invoice-container {
      padding: 0 !important;
      margin: 0 !important;
      border: none !important;
    }
    
    /* Content area with minimal margins */
    .content-area {
      padding-left: 0 !important;
      padding-right: 0 !important;
      overflow-x: hidden !important;
    }

    /* Center company logo */
    .company-logo-wrapper {
      display: flex !important;
      justify-content: center !important;
      width: 100% !important;
      margin-bottom: 1rem !important;
    }

    /* Company header logic - show logo or name based on availability */
    .company-info {
      display: flex;
      flex-direction: column;
      align-items: center !important;
      text-align: center !important;
      width: 100% !important;
    }
    
    /* When logo exists, hide company info text */
    .has-logo .company-info .company-text {
      display: none !important;
    }
    
    /* When no logo, show company info text */
    .no-logo .company-logo {
      display: none !important;
    }
    
    /* Hide INVOICE text */
    .invoice-title {
      display: none !important;
    }
    
    /* Change flex direction for header */
    .invoice-header {
      flex-direction: column !important;
      align-items: center !important;
      margin-bottom: 1rem !important;
    }
    
    .invoice-header > div {
      width: 100% !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
    }
    
    /* Table styling - full width and scrollable */
    .invoice-table-container {
      width: 100vw !important;
      overflow-x: auto !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      padding: 0 !important;
    }
    
    .invoice-table {
      font-size: 0.85rem !important;
      min-width: 100% !important;
      box-sizing: border-box !important;
    }
    
    /* Hide category column (1st column) */
    .invoice-table th:nth-child(1),
    .invoice-table td:nth-child(1) {
      display: none !important;
    }
    
    /* Hide description column (3rd column) */
    .invoice-table th:nth-child(3),
    .invoice-table td:nth-child(3) {
      display: none !important;
    }
    
    /* Hide price column (5th column) */
    .invoice-table th:nth-child(5),
    .invoice-table td:nth-child(5) {
      display: none !important;
    }
    
    /* Make product name column fill available space */
    .invoice-table th:nth-child(2),
    .invoice-table td:nth-child(2) {
      width: auto !important;
      max-width: 65% !important;
      min-width: 120px !important;
      white-space: normal !important;
      overflow-wrap: break-word !important;
      word-break: break-word !important;
      padding-left: 4px !important;
      padding-right: 4px !important;
    }
    
    /* Make quantity column very small (enough for 3 digits) */
    .invoice-table th:nth-child(4),
    .invoice-table td:nth-child(4) {
      width: 40px !important;
      min-width: 40px !important;
      max-width: 40px !important;
      font-size: 0.75rem !important;
      padding-left: 2px !important;
      padding-right: 2px !important;
      text-align: center !important;
    }
    
    /* Make total column appropriate size */
    .invoice-table th:nth-child(6),
    .invoice-table td:nth-child(6) {
      width: 70px !important;
      min-width: 70px !important;
      max-width: 70px !important;
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    .invoice-table th:nth-child(4) .full-text {
      display: none !important;
    }
    
    .invoice-table th:nth-child(4) .mobile-text {
      display: inline !important;
    }
    
    /* Adjust classic template specifically */
    .classic-table th:nth-child(2),
    .classic-table td:nth-child(2) {
      font-size: 0.75rem !important;
      width: auto !important;
      max-width: 65% !important;
    }
    
    .classic-table th:nth-child(2) .full-text {
      display: none !important;
    }
    
    .classic-table th:nth-child(2) .mobile-text {
      display: inline !important;
    }
    
    /* Hide price column in classic template */
    .classic-table th:nth-child(3),
    .classic-table td:nth-child(3) {
      display: none !important;
    }
    
    /* Adjust qty column in classic template */
    .classic-table th:nth-child(2),
    .classic-table td:nth-child(2) {
      width: 40px !important;
      min-width: 40px !important;
      max-width: 40px !important;
    }

    /* Reduce padding in table cells */
    .invoice-table th,
    .invoice-table td {
      padding: 6px 2px !important;
    }
    
    /* Payment options section - better mobile layout */
    .payment-options .flex {
      flex-direction: column !important;
    }
    
    .payment-options .ml-auto {
      margin-left: 0 !important;
      margin-top: 8px !important;
    }
    
    /* Better spacing for address sections */
    .bill-to-section {
      margin-top: 0.5rem !important;
      padding-bottom: 0.5rem !important;
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    /* Adjust margins and paddings for better mobile experience */
    .invoice-details {
      margin-top: 0.5rem !important;
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    /* More compact payment options */
    .payment-option {
      padding: 0.3rem !important;
      cursor: default !important; /* Disable cursor pointer on container to prevent accidental clicks */
    }
    
    /* Reduce font sizes globally for mobile */
    .invoice-container {
      font-size: 0.9rem !important;
    }
    
    /* Adjust company logo size */
    .company-logo {
      height: 70px !important;
      width: auto !important;
    }

    /* Make sure Qty shows on mobile */
    .full-text {
      display: none !important;
    }
    
    .mobile-text {
      display: inline !important;
    }
    
    /* Left align totals in mobile */
    .totals-section {
      text-align: left !important;
      width: 100% !important;
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    .totals-section > div {
      display: flex !important;
      flex-direction: row !important;
      justify-content: space-between !important;
      width: 100% !important;
    }
    
    /* Hide total from totals section when in mobile */
    .mobile-hide-total {
      display: none !important;
    }
    
    /* Ensure pay now buttons are properly clickable with no side effects */
    .pay-now-button {
      display: block !important;
      width: 100% !important;
      position: relative !important;
      z-index: 5 !important; /* Higher z-index to ensure it's clickable */
    }
    
    /* Make payment options less clickable as containers, only buttons clickable */
    .payment-option {
      pointer-events: none !important; /* Disable container click events */
    }
    
    .payment-option button,
    .payment-option a {
      pointer-events: auto !important; /* Re-enable events only on buttons */
    }
    
    /* Payment box styling for mobile */
    .payment-info-mobile {
      display: flex !important;
      flex-direction: column !important;
      width: 100% !important;
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    .payment-info-mobile .payment-amount-row {
      display: flex !important;
      justify-content: space-between !important;
      width: 100% !important;
      margin-bottom: 0.25rem !important;
    }
    
    /* Align payment amounts */
    .payment-info-amount {
      margin-left: auto !important;
      text-align: right !important;
    }
    
    /* Hide desktop payment info sections */
    .payment-info-desktop {
      display: none !important;
    }
    
    /* Show mobile payment info sections */
    .payment-info-mobile {
      display: flex !important;
    }
    
    /* Notes and terms sections */
    .notes-terms {
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    /* Adjust all vertical spacing to be more compact */
    .section-spacing {
      margin-top: 0.5rem !important;
      margin-bottom: 0.5rem !important;
    }
  }
`;

// Add the style element to the component
const MobileStyles: FC = () => (
  <style dangerouslySetInnerHTML={{ __html: mobileStyles }} />
);

export const InvoicePreview: FC<InvoicePreviewProps> = ({ 
  invoice, 
  companySettings, 
  className = '', 
  showPreviewWatermark = true,
  isPreviewAndSend = false // Default to false for backward compatibility
}) => {
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isACHPaymentModalOpen, setIsACHPaymentModalOpen] = useState(false);
  const [savedPaymentMethod, setSavedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [savedACHPaymentMethod, setSavedACHPaymentMethod] = useState<PaymentMethod | null>(null);
  const [isLoadingPaymentMethod, setIsLoadingPaymentMethod] = useState(false);
  const [useNewPaymentMethod, setUseNewPaymentMethod] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  
  // Add ref for payment options section
  const paymentOptionsRef = useRef<HTMLDivElement>(null);
  
  const invoiceSettings: InvoiceSettings = {
    ...defaultInvoiceSettings,
    ...((companySettings.invoiceSettings || {}) as Partial<InvoiceSettings>)
  };

  const template = invoiceSettings.template;
  const colors = invoiceSettings.colors;

  // Check if ACH is enabled at the gateway level
  const isAchGatewayEnabled = useMemo(() => isAchPaymentAvailable(companySettings as CompanySettings), [companySettings]);

  // ACH is only available if BOTH the gateway enables it AND the invoice includes it as a payment method
  const isAchAvailableForInvoice = useMemo(() => {
    return isAchGatewayEnabled && Array.isArray(invoice.paymentMethods) && invoice.paymentMethods.includes('ach');
  }, [isAchGatewayEnabled, invoice.paymentMethods]);

  // Helper function to check if platform fees should be displayed in the totals section
  const shouldShowPlatformFee = (isPaymentSection: boolean = false) => {
    // For payment section calculations, always use the normal platform fee logic
    if (isPaymentSection) {
      return invoiceSettings.enablePlatformFee && (companySettings.platformFee || 0) > 0;
    }

    // Don't show platform fee if it's explicitly set to 0 on the invoice (happens after bank payment)
    if (invoice.platformFee === 0) {
      return false;
    }
    
    // Don't show platform fee if the invoice was paid with a bank account/ACH
    if (invoice.status === 'paid' && (paymentMethod === 'ach' || invoice.paymentMethod === 'ach')) {
      return false;
    }
    
    // Don't show platform fee in totals if ACH is enabled as a payment option and invoice is not paid
    // This allows customers to see the amount without platform fees when ACH is available
    // Only hide if ACH is actually available at the gateway level
    if (isAchAvailableForInvoice && invoice.status !== 'paid') {
      return false;
    }
    
    // Otherwise show platform fee if it's enabled in settings and there is a fee percentage
    return invoiceSettings.enablePlatformFee && (companySettings.platformFee || 0) > 0;
  };
  
  // Function to get template-specific styles
  const getTemplateStyles = () => {
    switch (template) {
      case 'professional':
        return {
          container: 'bg-white border-t-4 border-blue-700',
          header: 'flex justify-between items-start mb-16',
          title: 'text-4xl font-bold text-right mb-2 text-blue-800',
          section: 'mb-12',
          addressSection: 'text-right text-sm mb-8',
          billToSection: 'grid grid-cols-2 gap-16 mb-12',
          billToTitle: 'text-sm font-bold uppercase mb-1 text-blue-700',
          table: 'w-full border-t border-b border-blue-200',
          tableHeader: 'text-left border-b border-blue-200 bg-blue-50',
          tableHeaderCell: 'py-3 px-4 text-sm font-semibold text-blue-800',
          tableRow: 'border-b border-blue-100',
          tableCell: 'py-3 px-4 text-sm',
          totalsSection: 'w-72 ml-auto space-y-1 mt-6',
          watermark: 'text-blue-100/20 text-9xl',
          paidWatermark: 'text-green-100/20 text-9xl',
          overdueWatermark: 'text-red-600 text-9xl'
        };
      case 'classic':
        return {
          container: 'bg-white',
          header: 'flex justify-between items-start mb-8',
          title: 'text-5xl uppercase font-bold text-gray-900 tracking-tight mb-4',
          section: 'mb-6',
          addressSection: 'mb-6',
          billToSection: 'grid grid-cols-2 gap-8 mb-6',
          table: 'w-full border-collapse',
          tableHeader: 'text-left bg-gray-700 text-white',
          tableHeaderCell: 'py-3 px-4 font-bold',
          tableRow: 'border-b border-gray-300',
          tableCell: 'py-3 px-4',
          totalsSection: 'w-72 space-y-2 ml-auto',
          watermark: 'text-gray-700/10 text-9xl font-bold',
          paidWatermark: 'text-green-700/20 text-9xl font-bold',
          overdueWatermark: 'text-red-700/20 text-9xl font-bold'
        };
      default:
        // Default to professional template
        return {
          container: 'bg-white border-t-4 border-blue-700',
          header: 'flex justify-between items-start mb-16',
          title: 'text-4xl font-bold text-right mb-2 text-blue-800',
          section: 'mb-12',
          addressSection: 'text-right text-sm mb-8',
          billToSection: 'grid grid-cols-2 gap-16 mb-12',
          billToTitle: 'text-sm font-bold uppercase mb-1 text-blue-700',
          table: 'w-full border-t border-b border-blue-200',
          tableHeader: 'text-left border-b border-blue-200 bg-blue-50',
          tableHeaderCell: 'py-3 px-4 text-sm font-semibold text-blue-800',
          tableRow: 'border-b border-blue-100',
          tableCell: 'py-3 px-4 text-sm',
          totalsSection: 'w-72 ml-auto space-y-1 mt-6',
          watermark: 'text-blue-100/20 text-9xl',
          paidWatermark: 'text-green-100/20 text-9xl',
          overdueWatermark: 'text-red-600 text-9xl'
        };
    }
  };

  const formatInvoiceNumber = () => {
    // If we have an actual invoice number from the invoice, use that directly
    if (invoice.invoiceNumber) {
      return invoice.invoiceNumber;
    }
    
    // Otherwise, generate a formatted number from settings (for new invoice previews)
    let formattedNumber = '';
    
    // Add prefix if set
    if (invoiceSettings.invoicePrefix) {
      formattedNumber += `${invoiceSettings.invoicePrefix}-`;
    }
    
    // Add date prefix if enabled
    if (invoiceSettings.enableDatePrefix) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      formattedNumber += `${year}${month}-`;
    }
    
    // Add the number with padding
    const numberPart = String(invoiceSettings.invoiceStartingNumber || 1000);
    const paddedNumber = numberPart.padStart(invoiceSettings.zeroPadding || 5, '0');
    formattedNumber += paddedNumber;
    
    // Apply the number format template if available
    if (invoiceSettings.numberFormat && invoiceSettings.numberFormat !== 'INV-{NUMBER}') {
      // Replace {NUMBER} placeholder with the generated number
      return invoiceSettings.numberFormat.replace('{NUMBER}', formattedNumber);
    }
    
    return formattedNumber;
  };

  const styles = getTemplateStyles();
  const formattedInvoiceNumber = formatInvoiceNumber();

  /**
   * Format a Firestore timestamp, Date object, or 'YYYY-MM-DD' string as a local date
   */
  const formatDate = (dateValue: any): string => {
    if (!dateValue) return '';
    try {
      // If it's a string in YYYY-MM-DD format, parse as local date
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [year, month, day] = dateValue.split('-').map(Number);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          const date = new Date(year, month - 1, day, 12, 0, 0);
          return date.toLocaleDateString();
        }
      }
      // Handle Firestore Timestamp objects
      if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
        const date = new Date(dateValue.seconds * 1000);
        return date.toLocaleDateString();
      }
      // Handle standard Date objects or ISO strings
      const date = new Date(dateValue);
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Function to render recurring details
  const renderRecurringDetails = () => {
    if (!invoice.invoiceType || invoice.invoiceType === 'standard') {
      return null;
    }

    return (
      <div className="mt-8 pt-4 border-t text-sm" style={{ borderColor: colors.secondary }}>
        <h3 className="font-semibold mb-2" style={{ color: colors.headerText }}>
          Recurring Invoice Details
        </h3>
        
        {invoice.invoiceType === 'recurring' && invoice.recurringDetails && (
          <div style={{ color: colors.secondary }}>
            <p className="mb-1">
              <span className="font-medium">Billing Frequency:</span> {' '}
              {invoice.recurringDetails.frequency === 'weekly' ? 'Weekly' : 
               invoice.recurringDetails.frequency === 'monthly' ? 'Monthly' : 
               invoice.recurringDetails.frequency === 'quarterly' ? 'Quarterly' : 'Annual'}
            </p>
            {invoice.nextGenerationDate && (
              <p className="mb-1">
                <span className="font-medium">Next Billing Date:</span> {' '}
                {formatDate(invoice.nextGenerationDate)}
              </p>
            )}
            {invoice.recurringDetails.endDate && (
              <p className="mb-1">
                <span className="font-medium">End Date:</span> {' '}
                {formatDate(invoice.recurringDetails.endDate)}
              </p>
            )}
            {invoice.recurringDetails.remainingOccurrences !== undefined && (
              <p className="mb-1">
                <span className="font-medium">Remaining Occurrences:</span> {' '}
                {invoice.recurringDetails.remainingOccurrences}
              </p>
            )}
            <p className="mt-2 text-xs italic">
              By paying this invoice, you authorize future automatic payments according to this schedule.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Fetch saved payment methods when component mounts
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (!invoice.customerId || invoice.status === 'paid') return;
      
      setIsLoadingPaymentMethod(true);
      try {
        const { db } = await import('@/lib/firebase');
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const paymentMethodsRef = collection(db, 'paymentMethods');
        
        // Fetch credit card payment methods
        const creditCardQuery = query(
          paymentMethodsRef,
          where('customerId', '==', invoice.customerId),
          where('companyId', '==', invoice.companyId),
          where('type', '==', 'credit_card')
        );
        
        const creditCardSnapshot = await getDocs(creditCardQuery);
        
        if (!creditCardSnapshot.empty) {
          // Map all methods
          let methods = creditCardSnapshot.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as any[];
          // Filter by current default provider to avoid mixing tokens across gateways
          const defaultProvider = companySettings?.paymentGateways?.defaults?.card || 'nmi';
          methods = methods.filter(m => {
            if (m.provider) {
              if (m.provider !== defaultProvider) return false;
              if (m.provider === 'nmi') return !!m.nmiCustomerId;
              if (m.provider === 'stripe') return !!m.stripePaymentMethodId && !!m.stripeCustomerId;
              if (m.provider === 'payarc') return !!m.payarcPaymentToken || !!m.payarcCustomerRef;
              return false;
            }
            // Legacy docs without provider field
            if (defaultProvider === 'nmi') return !!m.nmiCustomerId;
            if (defaultProvider === 'stripe') return !!m.stripePaymentMethodId && !!m.stripeCustomerId;
            if (defaultProvider === 'payarc') return !!m.payarcPaymentToken || !!m.payarcCustomerRef;
            return false;
          });
          if (methods.length > 0) {
            // Prefer the default flag if present, else first method
            const preferred = methods.find(m => m.isDefault) || methods[0];
            console.log('Found saved credit card payment method for provider', defaultProvider, preferred);
            setSavedPaymentMethod(preferred as any);
          } else {
            console.log('No saved credit card payment method for current provider');
            setSavedPaymentMethod(null);
          }
        } else {
          console.log('No saved credit card payment method found for this customer');
          setSavedPaymentMethod(null);
        }

        // Fetch ACH payment methods if ACH is enabled at gateway level AND on the invoice
        if (isAchGatewayEnabled && Array.isArray(invoice.paymentMethods) && invoice.paymentMethods.includes('ach')) {
          const achQuery = query(
            paymentMethodsRef,
            where('customerId', '==', invoice.customerId),
            where('companyId', '==', invoice.companyId),
            where('type', '==', 'ach')
          );
          
          const achSnapshot = await getDocs(achQuery);
          
          if (!achSnapshot.empty) {
            const achPaymentMethodData = achSnapshot.docs[0].data() as PaymentMethod;
            const achPaymentMethod = {
              ...achPaymentMethodData,
              id: achSnapshot.docs[0].id
            };
            
            console.log('Found saved ACH payment method:', achPaymentMethod);
            setSavedACHPaymentMethod(achPaymentMethod);
          } else {
            console.log('No saved ACH payment method found for this customer');
            setSavedACHPaymentMethod(null);
          }
        }
      } catch (error) {
        console.error('Error fetching saved payment methods:', error);
        setSavedPaymentMethod(null);
        setSavedACHPaymentMethod(null);
      } finally {
        setIsLoadingPaymentMethod(false);
      }
    };
    
    fetchPaymentMethods();
  }, [invoice.customerId, invoice.companyId, invoice.status, invoice.paymentMethods, isAchGatewayEnabled]);

  // Fetch transaction details when the component mounts and the invoice is paid
  useEffect(() => {
    const fetchTransactionDetails = async () => {
      if (invoice.status === 'paid' && invoice.paymentTransactionId) {
        try {
          const { db } = await import('@/lib/firebase');
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          
          const transactionsRef = collection(db, 'transactions');
          const q = query(transactionsRef, where('transactionId', '==', invoice.paymentTransactionId));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const transactionData = querySnapshot.docs[0].data();
            // Set the payment method used for this transaction
            setPaymentMethod(transactionData.paymentMethod || null);
            console.log('Transaction was paid with:', transactionData.paymentMethod);
          }
        } catch (error) {
          console.error('Error fetching transaction details:', error);
        }
      }
    };
    
    fetchTransactionDetails();
  }, [invoice.status, invoice.paymentTransactionId]);

  const handlePayNowClick = () => {
    // Scroll to payment options at the bottom
    paymentOptionsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleACHPaymentClick = async () => {
    // First check if this customer has a saved ACH payment method
    if (invoice.customerId) {
      try {
        setIsLoadingPaymentMethod(true);
        const { db } = await import('@/lib/firebase');
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const paymentMethodsRef = collection(db, 'paymentMethods');
        const q = query(
          paymentMethodsRef,
          where('customerId', '==', invoice.customerId),
          where('companyId', '==', invoice.companyId),
          where('type', '==', 'ach')
        );
        
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          // Found a saved ACH payment method - but don't use it automatically
          // Always show the ACH payment modal to require the customer to re-enter bank info for security
          setIsLoadingPaymentMethod(false);
          setIsACHPaymentModalOpen(true);
        } else {
          // No saved payment method, show the ACH payment modal
          setIsLoadingPaymentMethod(false);
          setIsACHPaymentModalOpen(true);
        }
      } catch (error) {
        console.error('Error checking for saved ACH payment method:', error);
        setIsLoadingPaymentMethod(false);
        setIsACHPaymentModalOpen(true);
      }
    } else {
      // No customer ID, just show the ACH payment modal
      setIsACHPaymentModalOpen(true);
    }
  };

  // Handle payment with saved payment method
  const handlePaymentWithSavedMethod = async () => {
    if (!savedPaymentMethod) return;
    
    setIsProcessing(true);
    
    try {
      const defaultProvider = companySettings?.paymentGateways?.defaults?.card || 'nmi';
      const savedMethodProvider = (savedPaymentMethod as any)?.provider || defaultProvider;
      
      // Calculate platform fee amount
      const platformFeeAmount = invoiceSettings.enablePlatformFee && companySettings.platformFee > 0 ? 
        ((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100) : 0;

      // Use unified charge endpoint with saved method
      const amountToCharge = invoiceSettings.enablePlatformFee && (companySettings.platformFee || 0) > 0
        ? (invoice.subtotal || 0) + (invoice.taxAmount || 0) + platformFeeAmount
        : (invoice.subtotal || 0) + (invoice.taxAmount || 0);

      const unifiedResp = await fetch('/api/payments/charge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: invoice.companyId,
          amount: amountToCharge,
          currency: 'usd',
          source: 'saved',
          savedMethod: { id: savedPaymentMethod.id, provider: savedMethodProvider },
          orderId: invoice.invoiceNumber || '',
          description: `Payment for Invoice #${invoice.invoiceNumber || ''}`,
          invoiceId: invoice.id,
          customerId: invoice.customerId,
          platformFeeEnabled: invoiceSettings.enablePlatformFee,
          platformFeePercentage: companySettings.platformFee || 0,
          platformFeeAmount,
          salesTax: invoice.taxAmount || 0,
          kind: 'card',
        })
      });
      const result = await unifiedResp.json();
      if (!unifiedResp.ok || !result.success) {
        throw new Error(result?.error || 'Payment processing failed');
      }

      // Handle successful payment
      console.log('Payment successful with saved card:', result);
      
      // Extract transaction ID and card details
      const transactionId = result.transactionId || result.transactionid || result.transaction_id || '';
      const cardLast4 = result.last4 || result.cardLast4 || savedPaymentMethod?.last4 || '';
      
      // Unified charge API already records a standardized transaction server-side
      
      // Mark invoice as paid - import dynamically to avoid circular references
      try {
        const { updateInvoice } = await import('@/lib/api');
        
        if (invoice && invoice.id) {
          await updateInvoice(invoice.id, { 
            status: 'paid',
            platformFee: invoiceSettings.enablePlatformFee ? platformFeeAmount : 0,  // Add platform fee field
            paymentTransactionId: transactionId,
            activities: [{
              id: `payment_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
              type: 'paid',
              timestamp: new Date().toISOString(),
              description: `Invoice paid - Transaction ID: ${transactionId || 'N/A'}` + 
                (invoiceSettings.enablePlatformFee ? ` (Platform fee: ${formatCurrency(platformFeeAmount)})` : ''),
              performedBy: 'customer',
              metadata: {
                transactionId: transactionId,
                platformFee: platformFeeAmount,
                platformFeePercentage: companySettings.platformFee || 0
              }
            }]
          });
          console.log(`Invoice ${invoice.id} status updated to 'paid'`);
        }
      } catch (updateError) {
        console.error('Error updating invoice status:', updateError);
      }
      
      // Show detailed payment confirmation dialog
      const { showTransactionSuccess } = await import('@/lib/notifications');
      
      showTransactionSuccess({
        transactionid: transactionId,
        amount: invoice.total || 0,
        cardLast4: cardLast4,
        additionalMessage: `Invoice #${invoice.invoiceNumber} has been marked as paid.`,
        onClose: () => {
          // Reload the page when the user clicks OK
          window.location.reload();
        }
      });
      
      // No need to call handlePaymentSuccess since we're already showing a notification
      // and handling the page reload in the onClose callback
    } catch (error) {
      console.error('Error processing payment with saved card:', error);
      setIsProcessing(false);
      
      // Get the detailed response if available
      let responseText = 'Payment processing failed';
      
      if (error instanceof Error) {
        responseText = error.message;
      }
      
      try {
        // Create a failed transaction record
        const { createFailedTransaction } = await import('@/lib/transactions');
        
        await createFailedTransaction({
          customerId: invoice.customerId || '',
          customerName: invoice.customerName || '',
          amount: invoice.total || 0,
          transactionId: `failed_${Date.now()}`,
          failureReason: responseText,
          companyId: invoice.companyId || '',
          paymentMethod: 'credit_card',
          cardLast4: savedPaymentMethod?.last4 || '',
          invoiceId: invoice.id,
          invoiceNumber: invoice.invoiceNumber || '',
          description: `Failed payment for Invoice #${invoice.invoiceNumber}`,
          additionalMetadata: {
            errorDetails: responseText
          }
        });
        
        console.log('Created failed transaction record for invoice:', invoice.id);
        
        // Add a failed payment activity to the invoice
        if (invoice && invoice.id) {
          const { updateInvoice } = await import('@/lib/api');
          
          await updateInvoice(invoice.id, {
            activities: [{
              id: `payment_failed_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
              type: 'payment_failed',
              timestamp: new Date().toISOString(),
              description: `Payment failed - ${responseText}`,
              performedBy: 'customer',
              metadata: {
                errorDetails: responseText
              }
            }]
          });
        }
        
        // Send email notification if customer has an email
        if (invoice.customerEmail) {
          try {
            const { sendCardOnFileTransactionEmail } = await import('@/lib/emailNotifications');
            
            await sendCardOnFileTransactionEmail({
              transactionId: `failed_${Date.now()}`,
              amount: invoice.total || 0,
              status: 'declined',
              cardLast4: savedPaymentMethod?.last4 || '',
              responseText: responseText,
              customerEmail: invoice.customerEmail,
              customerName: invoice.customerName || '',
              companyId: invoice.companyId || '',
              invoiceNumber: invoice.invoiceNumber,
              invoiceId: invoice.id
            });
            
            console.log('Sent failed payment email notification');
          } catch (emailError) {
            console.error('Error sending failed payment email notification:', emailError);
            // Continue with process even if email fails
          }
        }
      } catch (recordError) {
        console.error('Error creating failed transaction record:', recordError);
      }
      
      // Show a user-friendly error notification
      const { showCardOnFileTransactionNotification } = await import('@/lib/notifications');
      
      showCardOnFileTransactionNotification({
        transactionid: 'ERROR',
        amount: invoice.total || 0,
        status: 'declined',
        cardLast4: savedPaymentMethod?.last4 || '',
        responsetext: responseText,
        additionalMessage: 'There was a problem processing your payment. Please try again or use a different payment method.',
        onClose: () => {
          // Don't reload the page on error, allow the user to try again
          setIsProcessing(false);
        }
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNewPaymentMethodClick = () => {
    setUseNewPaymentMethod(true);
    setIsPaymentModalOpen(true);
  };

  // Handle payment success callback from PaymentModal
  const handlePaymentSuccess = (transactionId: string, cardLast4: string = '', paymentMethod: string = 'credit_card') => {
    console.log('Payment success callback received with transaction ID:', transactionId);
    
    // Verify the transaction and update the invoice status
    const verifyTransaction = async () => {
      try {
        // Validate transaction ID
        const validTransactionId = transactionId || 'unknown';
        
        // Check if the payment resulted in a transaction record
        // If not, create one for consistency
        const { db } = await import('@/lib/firebase');
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        
        // Only proceed if we have a company ID
        if (!invoice.companyId) {
          console.error('Cannot verify transaction: Missing company ID');
          return;
        }
        
        // Get the customer company name if possible
        let customerCompanyName = invoice.customerCompanyName || '';
        if (invoice.customerId && !customerCompanyName) {
          const customersRef = collection(db, 'customers');
          const customerQuery = query(customersRef, where('id', '==', invoice.customerId));
          const customerSnapshot = await getDocs(customerQuery);
          
          if (!customerSnapshot.empty) {
            const customerData = customerSnapshot.docs[0].data();
            customerCompanyName = customerData.companyName || '';
          }
        }
        
        // Check if a transaction record exists for this transaction ID
        const transactionsRef = collection(db, 'transactions');
        const q = query(transactionsRef, where('transactionId', '==', validTransactionId));
        const querySnapshot = await getDocs(q);
        
        // If no transaction record exists, create one
        if (querySnapshot.empty) {
          console.log('No transaction record found, creating one...');
          
          try {
            // Import the transaction creation utility
            const { createInvoicePaymentTransaction } = await import('@/lib/transactions');
            
            // Use standardized invoice payment transaction creation
            await createInvoicePaymentTransaction({
              customerId: invoice.customerId || '',
              customerName: invoice.customerName || '',
              amount: invoice.total || 0,
              status: 'paid',
              transactionId: validTransactionId,
              companyId: invoice.companyId || '',
              customerCompanyName,
              salesTax: invoice.taxAmount || 0,
              paymentMethod: paymentMethod,
              cardLast4: cardLast4,
              invoiceId: invoice.id,
              invoiceNumber: invoice.invoiceNumber || '',
              // Add platform fee information for credit card payments
              platformFeeEnabled: invoiceSettings.enablePlatformFee === true,
              platformFeePercentage: companySettings.platformFee || 0,
              platformFeeAmount: invoiceSettings.enablePlatformFee ? 
                ((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100) : 0
            });
            
            console.log('Created missing transaction record for invoice:', invoice.id);
          } catch (error: any) {
            console.error('Error creating transaction record:', error);
          }
        }
      
        // Always update the invoice status to paid
        if (invoice && invoice.id) {
          try {
            // Instead of direct Firestore update, use the updateInvoice API function
            // This will ensure thank you emails are sent properly
            const { updateInvoice } = await import('@/lib/api');
            await updateInvoice(invoice.id, {
              status: 'paid',
              paymentTransactionId: transactionId
            });
            console.log('Updated invoice status to paid for invoice:', invoice.id);
          } catch (updateError) {
            console.error('Error updating invoice status:', updateError);
          }
        }
        
        // Show a detailed payment confirmation dialog
        const { showTransactionSuccess } = await import('@/lib/notifications');
        
        // For bank payments (ACH), never include platform fees in the amount
        const isACHPayment = paymentMethod === 'ach';
        const amountToShow = isACHPayment 
          ? (invoice.subtotal || 0) + (invoice.taxAmount || 0)
          : (invoice.total || 0);
        
        showTransactionSuccess({
          transactionid: transactionId,
          amount: amountToShow,
          cardLast4: cardLast4,
          additionalMessage: `Invoice #${invoice.invoiceNumber} has been marked as paid.`,
          onClose: () => {
            // Reload the page when the user clicks OK
            window.location.reload();
          }
        });
        
      } catch (error) {
        console.error('Error verifying transaction:', error);
      }
    };
    
    verifyTransaction();
  };

  return (
    <div className="relative">
      {/* Add the mobile styles */}
      <MobileStyles />
      
      {/* Paid Watermark - Always show for paid invoices, regardless of preview mode */}
      {invoice.status && invoice.status === 'paid' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden z-10">
          <div className="transform rotate-45 translate-y-[-60%] text-[150px] font-bold opacity-20 text-emerald-600 select-none">
            PAID
          </div>
        </div>
      )}
      
      {/* Preview Watermark - only shown when in preview mode */}
      {showPreviewWatermark && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={cn(
            'transform -rotate-45 text-8xl font-bold select-none',
            styles.watermark
          )}>
            PREVIEW
          </div>
        </div>
      )}

      <Card className={`overflow-hidden ${className} relative invoice-card`}>
        <div className={cn('p-8 invoice-container', styles.container, {
          'has-logo': invoiceSettings.showLogo && companySettings.logo,
          'no-logo': !invoiceSettings.showLogo || !companySettings.logo
        })}>
          <div className="content-area">
            {/* Header */}
            {template === 'professional' ? (
              <div className={cn('flex justify-between items-start invoice-header', styles.header)}>
                <div className="flex-shrink-0 company-logo-wrapper">
                  {invoiceSettings.showLogo && companySettings.logo && (
                    <img
                      src={companySettings.logo}
                      alt="Company logo"
                      className="h-32 w-auto object-contain company-logo"
                      onError={(e) => {
                        // Hide the image on error instead of showing a broken image icon
                        e.currentTarget.style.display = 'none';
                        console.log('Failed to load company logo');
                      }}
                    />
                  )}
                </div>
                <div className="ml-4 company-info">
                  <p className="font-semibold company-text" style={{ color: colors.headerText }}>{companySettings.name}</p>
                  <p className="company-text">{companySettings.address?.street}</p>
                  <p className="company-text">{companySettings.address?.city}, {companySettings.address?.state} {companySettings.address?.zip}</p>
                  {invoiceSettings.showPhoneOnInvoice && (invoiceSettings.invoicePhone || companySettings.phone) && (
                    <p className="company-text">{invoiceSettings.invoicePhone || companySettings.phone}</p>
                  )}
                  {invoiceSettings.showEmailOnInvoice && (invoiceSettings.invoiceEmail || companySettings.email) && (
                    <p className="company-text">{invoiceSettings.invoiceEmail || companySettings.email}</p>
                  )}
                </div>
                <div className="text-right">
                  <h1 className={cn(styles.title, 'invoice-title')} style={{ color: colors.primary }}>INVOICE</h1>
                </div>
              </div>
            ) : template === 'classic' ? (
              <>
                <div className="text-center mb-6">
                  <h1 className="text-3xl uppercase font-bold tracking-wider invoice-title" style={{ color: colors.primary }}>
                    INVOICE
                  </h1>
                </div>
                <div className={cn('flex justify-between items-start invoice-header', styles.header)}>
                  <div className="flex-1">
                    <div className="flex items-start gap-6">
                      {invoiceSettings.showLogo && companySettings.logo && (
                        <img
                          src={companySettings.logo}
                          alt="Company logo"
                          className="h-24 object-contain mb-4 company-logo"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            console.log('Failed to load company logo');
                          }}
                        />
                      )}
                      <div className="mt-2 company-info">
                        <p className="font-bold company-name" style={{ color: colors.headerText }}>{companySettings.name}</p>
                        <p style={{ color: colors.secondary }}>{companySettings.address?.street}</p>
                        <p style={{ color: colors.secondary }}>
                          {companySettings.address?.city}, {companySettings.address?.state} {companySettings.address?.zip}
                        </p>
                        {invoiceSettings.showPhoneOnInvoice && (invoiceSettings.invoicePhone || companySettings.phone) && (
                          <p style={{ color: colors.secondary }}>{invoiceSettings.invoicePhone || companySettings.phone}</p>
                        )}
                        {invoiceSettings.showEmailOnInvoice && (invoiceSettings.invoiceEmail || companySettings.email) && (
                          <p style={{ color: colors.secondary }}>{invoiceSettings.invoiceEmail || companySettings.email}</p>
                        )}
                      </div>
                    </div>
                    <div className={cn(styles.billToSection, 'bill-to-section')}>
                      <h2 className={styles.billToTitle} style={{ color: colors.headerText }}>
                        Bill To
                      </h2>
                      <div style={{ color: colors.secondary }}>
                        {invoice.customerCompanyName ? (
                          <>
                            <p className="font-bold" style={{ color: colors.headerText }}>{invoice.customerCompanyName}</p>
                            {invoice.customerName && invoice.customerName.trim() !== '' && (
                              <p style={{ color: colors.secondary }}>{invoice.customerName}</p>
                            )}
                          </>
                        ) : (
                          <p className="font-medium" style={{ color: colors.headerText }}>{invoice.customerName}</p>
                        )}
                        {(invoice.customerAddress || invoice.street || invoice.city || invoice.state || invoice.zip) && (
                          <>
                            {/* Display street address - prefer dedicated street field if available */}
                            <p>{invoice.street || (invoice.customerAddress ? invoice.customerAddress.split(',')[0] : '')}</p>
                            
                            {/* Display city, state, zip - prefer dedicated fields if available */}
                            <p>
                              {/* City */}
                              {invoice.city || ''}
                              
                              {/* Separator if both city and state/zip exist */}
                              {invoice.city && (invoice.state || invoice.zip) ? ', ' : ''}
                              
                              {/* State */}
                              {invoice.state || ''}
                              
                              {/* Space before zip if state exists */}
                              {invoice.state && invoice.zip ? ' ' : ''}
                              
                              {/* Zip */}
                              {invoice.zip || ''}
                              
                              {/* If no dedicated fields, but we have customerAddress, try to parse it */}
                              {!invoice.city && !invoice.state && !invoice.zip && invoice.customerAddress && (() => {
                                // Parse the address string
                                const parts = invoice.customerAddress.split(',');
                                if (parts.length >= 2) {
                                  // City is usually second part
                                  const city = parts[1]?.trim() || '';
                                  
                                  // Extract state and zip from the last part
                                  const lastPart = parts[2]?.trim() || '';
                                  
                                  // Try more flexible patterns for state and zip
                                  let stateZipMatch = lastPart.match(/([A-Z]{2})\s*(\d{5}(?:-\d{4})?)/);
                                  
                                  if (!stateZipMatch) {
                                    const zipMatch = lastPart.match(/(\d{5}(?:-\d{4})?)/);
                                    if (zipMatch) {
                                      const stateMatch = lastPart.match(/([A-Z]{2})/);
                                      stateZipMatch = ['', stateMatch?.[1] || '', zipMatch[1]];
                                    }
                                  }
                                  
                                  let state = '';
                                  let zip = '';
                                  
                                  if (stateZipMatch) {
                                    state = stateZipMatch[1] || '';
                                    zip = stateZipMatch[2] || '';
                                  } else {
                                    const stateMatch = lastPart.match(/\b([A-Z]{2})\b/);
                                    const zipMatch = lastPart.match(/\b(\d{5}(?:-\d{4})?)\b/);
                                    
                                    state = stateMatch?.[1] || '';
                                    zip = zipMatch?.[1] || '';
                                    
                                    if (!state && !zip) {
                                      state = lastPart;
                                    }
                                  }
                                  
                                  return `${city ? city + ', ' : ''}${state} ${zip}`.trim();
                                }
                                
                                return parts.slice(1).join(',').trim();
                              })()}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right invoice-details">
                    <div className="space-y-1" style={{ color: colors.secondary }}>
                      <p><span className="font-medium" style={{ color: colors.headerText }}>{invoice.invoiceType === 'recurring' ? 'Template Number:' : 'Invoice #:'}</span> {formattedInvoiceNumber}</p>
                      <p><span className="font-medium" style={{ color: colors.headerText }}>Date:</span> {formatDate(invoice.date)}</p>
                      {invoice.dueDate && (
                        <p><span className="font-medium" style={{ color: colors.headerText }}>{invoice.invoiceType === 'recurring' ? 'First Payment Due Date:' : 'Due Date:'}</span> {formatDate(invoice.dueDate)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className={cn('flex justify-between items-start', styles.header)}>
                <div className="flex items-start gap-6">
                  <div>
                    {invoiceSettings.showLogo && companySettings.logo && (
                      <img
                        src={companySettings.logo}
                        alt="Company logo"
                        className="h-16 object-contain mb-2 company-logo"
                        onError={(e) => {
                          // Hide the image on error instead of showing a broken image icon
                          e.currentTarget.style.display = 'none';
                          console.log('Failed to load company logo');
                        }}
                      />
                    )}
                    <h1 className={styles.title} style={{ color: colors.primary }}>INVOICE</h1>
                  </div>
                  <div className="mt-2" style={{ color: colors.secondary }}>
                    <p className="font-semibold" style={{ color: colors.headerText }}>{companySettings.name}</p>
                    <p>{companySettings.address?.street}</p>
                    <p>
                      {companySettings.address?.city}, {companySettings.address?.state} {companySettings.address?.zip}
                    </p>
                    {invoiceSettings.showPhoneOnInvoice && (invoiceSettings.invoicePhone || companySettings.phone) && (
                      <p>{invoiceSettings.invoicePhone || companySettings.phone}</p>
                    )}
                    {invoiceSettings.showEmailOnInvoice && (invoiceSettings.invoiceEmail || companySettings.email) && (
                      <p>{invoiceSettings.invoiceEmail || companySettings.email}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="mb-1 text-xl font-semibold" style={{ color: colors.headerText }}>
                    {formattedInvoiceNumber}
                  </p>
                  <p className="text-gray-600">
                    Date: {formatDate(invoice.date)}
                  </p>
                  <p className="text-gray-600">
                    Due: {formatDate(invoice.dueDate)}
                  </p>
                </div>
              </div>
            )}

            {/* Bill To & Invoice Info Section */}
            {template === 'professional' ? (
              <div className={cn(styles.billToSection, 'bill-to-section')}>
                <div>
                  <h2 className={styles.billToTitle} style={{ color: colors.headerText }}>
                    Bill To
                  </h2>
                  <div style={{ color: colors.secondary }}>
                    {invoice.customerCompanyName ? (
                      <>
                        <p className="font-bold" style={{ color: colors.headerText }}>{invoice.customerCompanyName}</p>
                        {invoice.customerName && invoice.customerName.trim() !== '' && (
                          <p style={{ color: colors.secondary }}>{invoice.customerName}</p>
                        )}
                      </>
                    ) : (
                      <p className="font-medium" style={{ color: colors.headerText }}>{invoice.customerName}</p>
                    )}
                    {(invoice.customerAddress || invoice.street || invoice.city || invoice.state || invoice.zip) && (
                      <>
                        {/* Display street address - prefer dedicated street field if available */}
                        <p>{invoice.street || (invoice.customerAddress ? invoice.customerAddress.split(',')[0] : '')}</p>
                        
                        {/* Display city, state, zip - prefer dedicated fields if available */}
                        <p>
                          {/* City */}
                          {invoice.city || ''}
                          
                          {/* Separator if both city and state/zip exist */}
                          {invoice.city && (invoice.state || invoice.zip) ? ', ' : ''}
                          
                          {/* State */}
                          {invoice.state || ''}
                          
                          {/* Space before zip if state exists */}
                          {invoice.state && invoice.zip ? ' ' : ''}
                          
                          {/* Zip */}
                          {invoice.zip || ''}
                          
                          {/* If no dedicated fields, but we have customerAddress, try to parse it */}
                          {!invoice.city && !invoice.state && !invoice.zip && invoice.customerAddress && (() => {
                            // Parse the address string
                            const parts = invoice.customerAddress.split(',');
                            if (parts.length >= 2) {
                              // City is usually second part
                              const city = parts[1]?.trim() || '';
                              
                              // Extract state and zip from the last part
                              const lastPart = parts[2]?.trim() || '';
                              
                              // Try more flexible patterns for state and zip
                              let stateZipMatch = lastPart.match(/([A-Z]{2})\s*(\d{5}(?:-\d{4})?)/);
                              
                              if (!stateZipMatch) {
                                const zipMatch = lastPart.match(/(\d{5}(?:-\d{4})?)/);
                                if (zipMatch) {
                                  const stateMatch = lastPart.match(/([A-Z]{2})/);
                                  stateZipMatch = ['', stateMatch?.[1] || '', zipMatch[1]];
                                }
                              }
                              
                              let state = '';
                              let zip = '';
                              
                              if (stateZipMatch) {
                                state = stateZipMatch[1] || '';
                                zip = stateZipMatch[2] || '';
                              } else {
                                const stateMatch = lastPart.match(/\b([A-Z]{2})\b/);
                                const zipMatch = lastPart.match(/\b(\d{5}(?:-\d{4})?)\b/);
                                
                                state = stateMatch?.[1] || '';
                                zip = zipMatch?.[1] || '';
                                
                                if (!state && !zip) {
                                  state = lastPart;
                                }
                              }
                              
                              return `${city ? city + ', ' : ''}${state} ${zip}`.trim();
                            }
                            
                            return parts.slice(1).join(',').trim();
                          })()}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <h2 className={styles.billToTitle} style={{ color: colors.headerText }}>
                    Invoice Info
                  </h2>
                  <div className="space-y-1" style={{ color: colors.secondary }}>
                    <p><span className="font-medium" style={{ color: colors.headerText }}>Invoice #:</span> {formattedInvoiceNumber}</p>
                    <p><span className="font-medium" style={{ color: colors.headerText }}>Date:</span> {formatDate(invoice.date)}</p>
                    <p><span className="font-medium" style={{ color: colors.headerText }}>Due Date:</span> {formatDate(invoice.dueDate)}</p>
                  </div>
                </div>
              </div>
            ) : template === 'classic' ? (
              null
            ) : (
              <div className={cn('grid grid-cols-2 gap-8', styles.section)}>
                <div>
                  <h2 className={cn('mb-2', template === 'minimal' ? 'text-sm uppercase tracking-wide' : 'text-sm font-semibold uppercase')}
                      style={{ color: colors.headerText }}>
                    From
                  </h2>
                  <div style={{ color: colors.secondary }}>
                    <p className="font-semibold" style={{ color: colors.headerText }}>{companySettings.name}</p>
                    {/* Company address moved to header */}
                  </div>
                </div>
                <div>
                  <h2 className={cn('mb-2', template === 'minimal' ? 'text-sm uppercase tracking-wide' : 'text-sm font-semibold uppercase')}
                      style={{ color: colors.headerText }}>
                    Bill To
                  </h2>
                  <div style={{ color: colors.secondary }}>
                    {invoice.customerCompanyName ? (
                      <>
                        <p className="font-bold" style={{ color: colors.headerText }}>{invoice.customerCompanyName}</p>
                        {invoice.customerName && invoice.customerName.trim() !== '' && (
                          <p style={{ color: colors.secondary }}>{invoice.customerName}</p>
                        )}
                      </>
                    ) : (
                      <p className="font-medium" style={{ color: colors.headerText }}>{invoice.customerName}</p>
                    )}
                    {(invoice.customerAddress || invoice.street || invoice.city || invoice.state || invoice.zip) && (
                      <>
                        {/* Display street address - prefer dedicated street field if available */}
                        <p>{invoice.street || (invoice.customerAddress ? invoice.customerAddress.split(',')[0] : '')}</p>
                        
                        {/* Display city, state, zip - prefer dedicated fields if available */}
                        <p>
                          {/* City */}
                          {invoice.city || ''}
                          
                          {/* Separator if both city and state/zip exist */}
                          {invoice.city && (invoice.state || invoice.zip) ? ', ' : ''}
                          
                          {/* State */}
                          {invoice.state || ''}
                          
                          {/* Space before zip if state exists */}
                          {invoice.state && invoice.zip ? ' ' : ''}
                          
                          {/* Zip */}
                          {invoice.zip || ''}
                          
                          {/* If no dedicated fields, but we have customerAddress, try to parse it */}
                          {!invoice.city && !invoice.state && !invoice.zip && invoice.customerAddress && (() => {
                            // Parse the address string
                            const parts = invoice.customerAddress.split(',');
                            if (parts.length >= 2) {
                              // City is usually second part
                              const city = parts[1]?.trim() || '';
                              
                              // Extract state and zip from the last part
                              const lastPart = parts[2]?.trim() || '';
                              
                              // Try more flexible patterns for state and zip
                              let stateZipMatch = lastPart.match(/([A-Z]{2})\s*(\d{5}(?:-\d{4})?)/);
                              
                              if (!stateZipMatch) {
                                const zipMatch = lastPart.match(/(\d{5}(?:-\d{4})?)/);
                                if (zipMatch) {
                                  const stateMatch = lastPart.match(/([A-Z]{2})/);
                                  stateZipMatch = ['', stateMatch?.[1] || '', zipMatch[1]];
                                }
                              }
                              
                              let state = '';
                              let zip = '';
                              
                              if (stateZipMatch) {
                                state = stateZipMatch[1] || '';
                                zip = stateZipMatch[2] || '';
                              } else {
                                const stateMatch = lastPart.match(/\b([A-Z]{2})\b/);
                                const zipMatch = lastPart.match(/\b(\d{5}(?:-\d{4})?)\b/);
                                
                                state = stateMatch?.[1] || '';
                                zip = zipMatch?.[1] || '';
                                
                                if (!state && !zip) {
                                  state = lastPart;
                                }
                              }
                              
                              return `${city ? city + ', ' : ''}${state} ${zip}`.trim();
                            }
                            
                            return parts.slice(1).join(',').trim();
                          })()}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Line Items */}
            {template === 'professional' ? (
              <div className={cn(styles.section, "section-spacing")}>
                <div className="invoice-table-container">
                  <table className={cn(styles.table, 'invoice-table')}>
                    <thead>
                      <tr>
                        <th 
                          className={cn(styles.tableHeaderCell, 'w-1/6 text-left whitespace-nowrap')} 
                          style={{ 
                            color: colors.tableHeaderText,
                            backgroundColor: colors.tableHeader,
                            borderColor: colors.secondary 
                          }}
                        >
                          Category
                        </th>
                        <th 
                          className={cn(styles.tableHeaderCell, 'w-1/6 text-left whitespace-nowrap')} 
                          style={{ 
                            color: colors.tableHeaderText,
                            backgroundColor: colors.tableHeader,
                            borderColor: colors.secondary 
                          }}
                        >
                          Product Name
                        </th>
                        <th 
                          className={cn(styles.tableHeaderCell, 'w-2/6 text-left')} 
                          style={{ 
                            color: colors.tableHeaderText,
                            backgroundColor: colors.tableHeader,
                            borderColor: colors.secondary 
                          }}
                        >
                          Description
                        </th>
                        <th 
                          className={cn(styles.tableHeaderCell, 'text-center pr-4')} 
                          style={{ 
                            color: colors.tableHeaderText,
                            backgroundColor: colors.tableHeader,
                            borderColor: colors.secondary 
                          }}
                        >
                          <span>Qty</span>
                        </th>
                        <th 
                          className={cn(styles.tableHeaderCell, 'text-left pr-4')} 
                          style={{ 
                            color: colors.tableHeaderText,
                            backgroundColor: colors.tableHeader,
                            borderColor: colors.secondary 
                          }}
                        >
                          Price
                        </th>
                        <th 
                          className={cn(styles.tableHeaderCell, 'text-right pr-4')} 
                          style={{ 
                            color: colors.tableHeaderText,
                            backgroundColor: colors.tableHeader,
                            borderColor: colors.secondary 
                          }}
                        >
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.items?.map((item, index) => (
                        <tr key={item.id || index} className={styles.tableRow} style={{ borderColor: colors.secondary }}>
                          <td className={cn(styles.tableCell, 'w-1/6 text-left pl-4')} style={{ color: colors.secondary }}>{item.categoryName}</td>
                          <td className={cn(styles.tableCell, 'w-1/6 text-left pl-4')} style={{ color: colors.secondary }}>{item.productName}</td>
                          <td className={cn(styles.tableCell, 'w-2/6 text-left pl-4')} style={{ color: colors.secondary }}>{item.description}</td>
                          <td className={cn(styles.tableCell, 'text-center pr-4')} style={{ color: colors.secondary }}>{item.quantity}</td>
                          <td className={cn(styles.tableCell, 'text-left pr-4')} style={{ color: colors.secondary }}>{formatCurrency(item.unitPrice)}</td>
                          <td className={cn(styles.tableCell, 'text-right pr-4')} style={{ color: colors.secondary }}>{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className={cn(styles.section, "section-spacing")}>
                {template === 'classic' ? (
                  <div className="invoice-table-container">
                    <table className={cn(styles.table, 'invoice-table', 'classic-table')}>
                      <thead>
                        <tr>
                          <th className={cn(styles.tableHeaderCell, 'w-1/2')} style={{ backgroundColor: colors.tableHeader, color: colors.tableHeaderText }}>DESCRIPTION</th>
                          <th className={cn(styles.tableHeaderCell, 'text-left w-1/6')} style={{ backgroundColor: colors.tableHeader, color: colors.tableHeaderText }}>
                            <span className="full-text">QUANTITY</span>
                            <span className="mobile-text">QTY</span>
                          </th>
                          <th className={cn(styles.tableHeaderCell, 'text-left w-1/6')} style={{ backgroundColor: colors.tableHeader, color: colors.tableHeaderText }}>PRICE</th>
                          <th className={cn(styles.tableHeaderCell, 'text-right w-1/6')} style={{ backgroundColor: colors.tableHeader, color: colors.tableHeaderText }}>AMOUNT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.items?.map((item, index) => (
                          <tr key={item.id || index} className={styles.tableRow}>
                            <td className={cn(styles.tableCell)} style={{ color: colors.secondary }}>
                              <div className="font-bold" style={{ color: colors.primary }}>{item.productName}</div>
                              <div>{item.description}</div>
                            </td>
                            <td className={cn(styles.tableCell, 'text-left')} style={{ color: colors.secondary }}>{item.quantity}</td>
                            <td className={cn(styles.tableCell, 'text-left')} style={{ color: colors.secondary }}>{formatCurrency(item.unitPrice)}</td>
                            <td className={cn(styles.tableCell, 'text-right')} style={{ color: colors.secondary }}>{formatCurrency(item.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="invoice-table-container">
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th className={cn(styles.tableHeaderCell, 'w-1/6')} style={{ color: colors.headerText }}>Category</th>
                          <th className={cn(styles.tableHeaderCell, 'w-1/6')} style={{ color: colors.headerText }}>Product Name</th>
                          <th className={cn(styles.tableHeaderCell, 'w-2/6')} style={{ color: colors.headerText }}>Description</th>
                          <th className={cn(styles.tableHeaderCell, 'text-right')} style={{ color: colors.headerText }}>Quantity</th>
                          <th className={cn(styles.tableHeaderCell, 'text-right')} style={{ color: colors.headerText }}>Price</th>
                          <th className={cn(styles.tableHeaderCell, 'text-right')} style={{ color: colors.headerText }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.items?.map((item, index) => (
                          <tr key={item.id || index} className={styles.tableRow}>
                            <td className={cn(styles.tableCell, 'w-1/6')} style={{ color: colors.secondary }}>{item.categoryName}</td>
                            <td className={cn(styles.tableCell, 'w-1/6')} style={{ color: colors.secondary }}>{item.productName}</td>
                            <td className={cn(styles.tableCell, 'w-2/6')} style={{ color: colors.secondary }}>{item.description}</td>
                            <td className={cn(styles.tableCell, 'text-right')} style={{ color: colors.secondary }}>{item.quantity}</td>
                            <td className={cn(styles.tableCell, 'text-right')} style={{ color: colors.secondary }}>{formatCurrency(item.unitPrice)}</td>
                            <td className={cn(styles.tableCell, 'text-right')} style={{ color: colors.secondary }}>{formatCurrency(item.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Totals */}
            {template === 'professional' ? (
              <div className={cn('flex justify-end section-spacing', styles.section)}>
                <div className={cn(styles.totalsSection, 'totals-section')}>
                  <div className="flex justify-between py-2">
                    <span style={{ color: colors.secondary }}>Subtotal</span>
                    <span style={{ color: colors.secondary }}>{formatCurrency(invoice.subtotal || 0)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span style={{ color: colors.secondary }}>Taxes {invoice.taxRate ? `(${formatPercentage(invoice.taxRate, 0)})` : ''}</span>
                    <span style={{ color: colors.secondary }}>{formatCurrency(invoice.taxAmount || 0)}</span>
                  </div>
                  {/* Show platform fees when enabled in invoice settings */}
                  {shouldShowPlatformFee() && (
                    <div className="flex justify-between py-2">
                      <span style={{ color: colors.secondary }}>Platform Fee ({companySettings.platformFee || 0}%)</span>
                      <span style={{ color: colors.secondary }}>{formatCurrency(((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100))}</span>
                    </div>
                  )}
                  <div className={cn(
                    'flex justify-between py-2 border-t font-semibold mobile-hide-total',
                    (template as string) === 'classic' ? 'border-t-2 border-gray-700 mt-2' : ''
                  )}>
                    <span style={{ color: colors.headerText }}>TOTAL</span>
                    <span style={{ color: colors.totalText }}>
                      {shouldShowPlatformFee()
                        ? formatCurrency(
                            (invoice.subtotal || 0) + 
                            (invoice.taxAmount || 0) + 
                            ((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100)
                          )
                        : formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))
                      }
                    </span>
                  </div>
                  
                  {/* Pay Now Button - Only show if not paid and not in preview mode or if invoice is sent */}
                  {(invoice.status && invoice.status !== 'paid' && (!showPreviewWatermark || invoice.status === 'sent')) && (
                    <div className="mt-6 flex flex-col items-center">
                      <button
                        onClick={handlePayNowClick}
                        className="w-full px-6 py-3 rounded-md font-semibold text-lg transition-all hover:opacity-90"
                        style={{ 
                          backgroundColor: colors.buttonBackground || '#22C55E',
                          color: colors.linkText || '#FFFFFF'
                        }}
                      >
                        Pay Now
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className={cn('flex justify-end section-spacing', styles.section)}>
                <div className={cn(styles.totalsSection, 'totals-section')}>
                  <div className="flex justify-between py-2">
                    <span style={{ color: colors.secondary }}>Subtotal</span>
                    <span style={{ color: colors.secondary }}>{formatCurrency(invoice.subtotal || 0)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span style={{ color: colors.secondary }}>Taxes {invoice.taxRate ? `(${formatPercentage(invoice.taxRate, 0)})` : ''}</span>
                    <span style={{ color: colors.secondary }}>{formatCurrency(invoice.taxAmount || 0)}</span>
                  </div>
                  {/* Show platform fees when enabled in invoice settings */}
                  {shouldShowPlatformFee() && (
                    <div className="flex justify-between py-2">
                      <span style={{ color: colors.secondary }}>Platform Fee ({companySettings.platformFee || 0}%)</span>
                      <span style={{ color: colors.secondary }}>{formatCurrency(((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100))}</span>
                    </div>
                  )}
                  <div className={cn(
                    'flex justify-between py-2 border-t font-semibold mobile-hide-total',
                    (template as string) === 'classic' ? 'border-t-2 border-gray-700 mt-2' : ''
                  )}>
                    <span style={{ color: colors.headerText }}>TOTAL</span>
                    <span style={{ color: colors.totalText }}>
                      {shouldShowPlatformFee()
                        ? formatCurrency(
                            (invoice.subtotal || 0) + 
                            (invoice.taxAmount || 0) + 
                            ((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100)
                          )
                        : formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))
                      }
                    </span>
                  </div>
                  
                  {/* Pay Now Button - Only show if not paid and not in preview mode or if invoice is sent */}
                  {(invoice.status && invoice.status !== 'paid' && (!showPreviewWatermark || invoice.status === 'sent')) && (
                    <div className="mt-10 pt-6 border-t" style={{ borderColor: colors.secondary }}>
                      <h3 className="text-xl font-medium mb-6 text-center" style={{ color: colors.headerText }}>Payment</h3>
                      <div className="flex flex-col items-center">
                        <button
                          onClick={(e) => {
                            e.preventDefault(); // Prevent default
                            handlePayNowClick();
                          }}
                          className="px-8 py-3 rounded-md font-semibold text-lg transition-all hover:opacity-90 pay-now-button"
                          style={{ 
                            backgroundColor: colors.buttonBackground || '#22C55E',
                            color: colors.linkText || '#FFFFFF',
                            maxWidth: "400px",
                            width: "100%"
                          }}
                          disabled={isLoadingPaymentMethod || isProcessing}
                        >
                          {isProcessing ? (
                            "Processing payment..."
                          ) : isLoadingPaymentMethod ? (
                            "Loading payment options..."
                          ) : savedPaymentMethod && !useNewPaymentMethod ? (
                            `Pay with ${savedPaymentMethod.cardType || 'Card'} ${savedPaymentMethod.last4 || 'XXXX'}`
                          ) : (
                            "Pay Now"
                          )}
                        </button>
                        
                        {/* Link to use new payment method when card is on file */}
                        {savedPaymentMethod && !useNewPaymentMethod && !isProcessing && (
                          <button
                            onClick={(e) => {
                              e.preventDefault(); // Prevent default
                              handleNewPaymentMethodClick();
                            }}
                            className="mt-3 text-sm underline text-center hover:text-blue-700 transition-colors"
                            style={{ color: colors.primary }}
                          >
                            Pay using a new Payment Method
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes & Terms */}
            {(invoice.notes || invoiceSettings.customFooterText) && invoiceSettings.showNotesOnInvoice && (
              <div className="mt-8 pt-4 border-t text-sm notes-terms section-spacing">
                {invoice.notes && invoiceSettings.showNotesOnInvoice && (
                  <div className="mb-4">
                    <h3 className="font-semibold mb-2" style={{ color: colors.headerText }}>Notes</h3>
                    <p style={{ color: colors.secondary }}>{invoice.notes}</p>
                  </div>
                )}
                {invoiceSettings.customFooterText && invoiceSettings.showTermsOnInvoice && (
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: colors.headerText }}>Terms & Conditions</h3>
                    <p style={{ color: colors.secondary }}>{invoiceSettings.customFooterText}</p>
                  </div>
                )}
              </div>
            )}

            {/* Recurring Details */}
            {renderRecurringDetails()}

            {/* Payment Options - Added at the bottom of the invoice */}
            {(invoice.status && invoice.status !== 'paid' && (!showPreviewWatermark || invoice.status === 'sent')) && (
              <div className="mt-10 pt-6 border-t payment-options" ref={paymentOptionsRef} style={{ borderColor: colors.secondary }}>
                {isAchAvailableForInvoice ? (
                  <div className="space-y-8">
                    <div className="text-center">
                      <h3 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: colors.headerText }}>
                        {isPreviewAndSend ? "Process Payment on behalf of customer" : "Select Payment Method"}
                      </h3>
                    </div>

                    {/* Enhanced Payment Method Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
                      {/* Card on file option - only show if available */}
                      {savedPaymentMethod && !useNewPaymentMethod && (
                        <div 
                          className={`group relative bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border-2 border-gray-100 hover:border-gray-300 overflow-hidden ${isProcessing ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}
                          onClick={() => !isProcessing && handlePaymentWithSavedMethod()}
                        >
                          {isProcessing && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                              <div className="flex flex-col items-center space-y-2">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <span className="text-sm font-medium text-gray-700">Processing...</span>
                              </div>
                            </div>
                          )}
                          <div className="pt-2">
                            <h4 className="text-xl font-bold mb-3" style={{ color: colors.headerText }}>
                              Card on File
                            </h4>
                            
                            <div className="space-y-3 mb-6">
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-sm"></div>
                                <span className="text-sm font-semibold" style={{ color: colors.primary }}>
                                  {savedPaymentMethod.cardType?.toUpperCase() || 'CARD'} •••• {savedPaymentMethod.last4}
                                </span>
                              </div>
                              
                              <div className="flex items-center space-x-2 text-xs text-gray-500">
                                <Shield className="w-3 h-3" />
                                <span>
                                  Securely saved
                                  {(savedPaymentMethod as any).provider && (
                                    <>
                                      {' '}• Provider: {(savedPaymentMethod as any).provider.toUpperCase()}
                                    </>
                                  )}
                                </span>
                              </div>
                            </div>
                            
                            {/* Pricing breakdown */}
                            <div className="space-y-2 mb-6 p-3 bg-gray-50 rounded-lg">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Subtotal:</span>
                                <span className="font-medium">{formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))}</span>
                              </div>
                              {shouldShowPlatformFee(true) && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Platform fee:</span>
                                  <span className="font-medium">{formatCurrency(((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100))}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                                <span>Total:</span>
                                <span style={{ color: colors.primary }}>
                                  {shouldShowPlatformFee(true)
                                    ? formatCurrency(
                                        (invoice.subtotal || 0) + 
                                        (invoice.taxAmount || 0) + 
                                        ((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100)
                                      )
                                    : formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))
                                  }
                                </span>
                              </div>
                            </div>
                            
                            <button 
                              className="w-full py-4 rounded-xl font-bold text-white transition-all duration-300 hover:opacity-90 hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ backgroundColor: colors.buttonBackground || '#3B82F6' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isProcessing) {
                                  handlePaymentWithSavedMethod();
                                }
                              }}
                              disabled={isProcessing}
                            >
                              {isProcessing ? 'Processing...' : 'Pay Now'}
                            </button>
                          </div>

                          {/* Hover effect gradient */}
                          <div className="absolute inset-0 bg-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
                        </div>
                      )}

                      {/* Bank Account on file option - only show if available */}
                      {savedACHPaymentMethod && (
                        <div 
                          className="group relative bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border-2 border-gray-100 hover:border-gray-300 cursor-pointer overflow-hidden"
                          onClick={() => handleACHPaymentClick()}
                        >
                          <div className="pt-2">
                            <h4 className="text-xl font-bold mb-3" style={{ color: colors.headerText }}>
                              Bank Account on File
                            </h4>
                            
                            <div className="space-y-3 mb-6">
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-sm"></div>
                                <span className="text-sm font-semibold text-green-700">
                                  {savedACHPaymentMethod.accountType?.toUpperCase() || 'ACCOUNT'} •••• {savedACHPaymentMethod.last4}
                                </span>
                              </div>
                              
                              <div className="flex items-center space-x-2 text-xs text-green-600">
                                <Shield className="w-3 h-3" />
                                <span>Securely saved • No platform fees</span>
                              </div>
                            </div>
                            
                            {/* Pricing breakdown */}
                            <div className="space-y-2 mb-6 p-3 bg-green-50 rounded-lg">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Subtotal:</span>
                                <span className="font-medium">{formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))}</span>
                              </div>
                              <div className="flex justify-between text-sm text-green-600">
                                <span>Platform fee:</span>
                                <span className="font-medium">$0.00</span>
                              </div>
                              <div className="flex justify-between text-base font-bold pt-2 border-t border-green-200">
                                <span>Total:</span>
                                <span className="text-green-700">{formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))}</span>
                              </div>
                            </div>
                            
                            <button 
                              className="w-full py-4 rounded-xl font-bold text-white bg-green-600 hover:bg-green-700 transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleACHPaymentClick();
                              }}
                            >
                              Pay with Bank
                            </button>
                          </div>

                          {/* Hover effect gradient */}
                          <div className="absolute inset-0 bg-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
                        </div>
                      )}
                      
                      {/* New card option */}
                      <div 
                        className="group relative bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border-2 border-gray-100 hover:border-gray-300 cursor-pointer overflow-hidden"
                        onClick={() => {
                          if (savedPaymentMethod) {
                            setUseNewPaymentMethod(true);
                          }
                          setIsPaymentModalOpen(true);
                        }}
                      >
                        <div className="pt-2">
                          <h4 className="text-xl font-bold mb-3" style={{ color: colors.headerText }}>
                            Use a New Card
                          </h4>
                          
                          <div className="space-y-3 mb-6">
                            <p className="text-sm text-gray-600">
                              {isPreviewAndSend ? "Process with a new customer card" : "Pay securely with your card"}
                            </p>
                            
                            <div className="flex items-center space-x-2 text-xs text-purple-600">
                              <Shield className="w-3 h-3" />
                              <span>256-bit SSL encryption</span>
                            </div>
                          </div>
                          
                          {/* Pricing breakdown */}
                          <div className="space-y-2 mb-6 p-3 bg-gray-50 rounded-lg">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Subtotal:</span>
                              <span className="font-medium">{formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))}</span>
                            </div>
                            {shouldShowPlatformFee(true) && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Platform fee:</span>
                                <span className="font-medium">{formatCurrency(((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100))}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                              <span>Total:</span>
                              <span style={{ color: colors.primary }}>
                                {shouldShowPlatformFee(true)
                                  ? formatCurrency(
                                      (invoice.subtotal || 0) + 
                                      (invoice.taxAmount || 0) + 
                                      ((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100)
                                    )
                                  : formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))
                                }
                              </span>
                            </div>
                          </div>
                          
                          <button 
                            className="w-full py-4 rounded-xl font-bold text-white transition-all duration-300 hover:opacity-90 hover:shadow-lg transform hover:-translate-y-0.5"
                            style={{ backgroundColor: colors.buttonBackground || '#8B5CF6' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (savedPaymentMethod) {
                                setUseNewPaymentMethod(true);
                              }
                              setIsPaymentModalOpen(true);
                            }}
                          >
                            Pay with New Card
                          </button>
                        </div>

                        {/* Hover effect gradient */}
                        <div className="absolute inset-0 bg-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
                      </div>
                      
                      {/* New Bank Account option - only show if no saved ACH method */}
                      {!savedACHPaymentMethod && (
                        <div 
                          className="group relative bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border-2 border-gray-100 hover:border-gray-300 cursor-pointer overflow-hidden"
                          onClick={() => handleACHPaymentClick()}
                        >
                          <div className="pt-2">
                            <h4 className="text-xl font-bold mb-3" style={{ color: colors.headerText }}>
                              Bank Account
                            </h4>
                            
                            <div className="space-y-3 mb-6">
                              <p className="text-sm text-gray-600">
                                {isPreviewAndSend ? "Connect customer's bank account" : "Connect your bank account"}
                              </p>
                              
                              <div className="flex items-center space-x-2 text-xs text-green-600">
                                <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                                <span className="font-semibold">No platform fees</span>
                              </div>
                            </div>
                            
                            {/* Pricing breakdown */}
                            <div className="space-y-2 mb-6 p-3 bg-green-50 rounded-lg">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Subtotal:</span>
                                <span className="font-medium">{formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))}</span>
                              </div>
                              <div className="flex justify-between text-sm text-green-600">
                                <span>Platform fee:</span>
                                <span className="font-medium">$0.00</span>
                              </div>
                              <div className="flex justify-between text-base font-bold pt-2 border-t border-green-200">
                                <span>Total:</span>
                                <span className="text-green-700">{formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))}</span>
                              </div>
                            </div>
                            
                            <button 
                              className="w-full py-4 rounded-xl font-bold text-white bg-green-600 hover:bg-green-700 transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleACHPaymentClick();
                              }}
                            >
                              Pay with Bank
                            </button>
                          </div>

                          {/* Hover effect gradient */}
                          <div className="absolute inset-0 bg-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
                        </div>
                      )}
                    </div>

                    {/* Security and Trust Indicators */}
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center space-x-6 text-xs text-gray-500 bg-gray-50 px-6 py-3 rounded-full">
                        <div className="flex items-center space-x-1">
                          <Shield className="w-3 h-3" />
                          <span>SSL Secured</span>
                        </div>
                        <div className="w-px h-3 bg-gray-300"></div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>Instant Processing</span>
                        </div>
                        <div className="w-px h-3 bg-gray-300"></div>
                        <span>PCI Compliant</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Credit Card Only Layout - No ACH */
                  <div className="space-y-8">
                    <div className="text-center">
                      <h3 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: colors.headerText }}>
                        {isPreviewAndSend ? "Process Payment on behalf of customer" : "Select Payment Method"}
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
                      {/* Card on file option - only show if available */}
                      {savedPaymentMethod && !useNewPaymentMethod && (
                        <div 
                          className={`group relative bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border-2 border-gray-100 hover:border-gray-300 overflow-hidden ${isProcessing ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}
                          onClick={() => !isProcessing && handlePaymentWithSavedMethod()}
                        >
                          {isProcessing && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                              <div className="flex flex-col items-center space-y-2">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <span className="text-sm font-medium text-gray-700">Processing...</span>
                              </div>
                            </div>
                          )}
                          <div className="pt-2">
                            <h4 className="text-xl font-bold mb-3" style={{ color: colors.headerText }}>
                              Card on File
                            </h4>
                            
                            <div className="space-y-3 mb-6">
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-sm"></div>
                                <span className="text-sm font-semibold" style={{ color: colors.primary }}>
                                  {savedPaymentMethod.cardType?.toUpperCase() || 'CARD'} •••• {savedPaymentMethod.last4}
                                </span>
                              </div>
                              
                              <div className="flex items-center space-x-2 text-xs text-gray-500">
                                <Shield className="w-3 h-3" />
                                <span>Securely saved</span>
                              </div>
                            </div>
                            
                            {/* Pricing breakdown */}
                            <div className="space-y-2 mb-6 p-3 bg-gray-50 rounded-lg">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Subtotal:</span>
                                <span className="font-medium">{formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))}</span>
                              </div>
                              {shouldShowPlatformFee(true) && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Platform fee:</span>
                                  <span className="font-medium">{formatCurrency(((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100))}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                                <span>Total:</span>
                                <span style={{ color: colors.primary }}>
                                  {shouldShowPlatformFee(true)
                                    ? formatCurrency(
                                        (invoice.subtotal || 0) + 
                                        (invoice.taxAmount || 0) + 
                                        ((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100)
                                      )
                                    : formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))
                                  }
                                </span>
                              </div>
                            </div>
                            
                            <button 
                              className="w-full py-4 rounded-xl font-bold text-white transition-all duration-300 hover:opacity-90 hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ backgroundColor: colors.buttonBackground || '#3B82F6' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isProcessing) {
                                  handlePaymentWithSavedMethod();
                                }
                              }}
                              disabled={isProcessing}
                            >
                              {isProcessing ? 'Processing...' : 'Pay Now'}
                            </button>
                          </div>

                          {/* Hover effect gradient */}
                          <div className="absolute inset-0 bg-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
                        </div>
                      )}
                      
                      {/* New card option */}
                      <div 
                        className="group relative bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border-2 border-gray-100 hover:border-gray-300 cursor-pointer overflow-hidden"
                        onClick={() => {
                          if (savedPaymentMethod) {
                            setUseNewPaymentMethod(true);
                          }
                          setIsPaymentModalOpen(true);
                        }}
                      >
                        <div className="pt-2">
                          <h4 className="text-xl font-bold mb-3" style={{ color: colors.headerText }}>
                            Credit Card
                          </h4>
                          
                          <div className="space-y-3 mb-6">
                            <p className="text-sm text-gray-600">
                              {isPreviewAndSend ? "Process with a new customer card" : "Pay securely with your card"}
                            </p>
                            
                            <div className="flex items-center space-x-2 text-xs text-purple-600">
                              <Shield className="w-3 h-3" />
                              <span>256-bit SSL encryption</span>
                            </div>
                          </div>
                          
                          {/* Pricing breakdown */}
                          <div className="space-y-2 mb-6 p-3 bg-gray-50 rounded-lg">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Subtotal:</span>
                              <span className="font-medium">{formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))}</span>
                            </div>
                            {shouldShowPlatformFee(true) && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Platform fee:</span>
                                <span className="font-medium">{formatCurrency(((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100))}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                              <span>Total:</span>
                              <span style={{ color: colors.primary }}>
                                {shouldShowPlatformFee(true)
                                  ? formatCurrency(
                                      (invoice.subtotal || 0) + 
                                      (invoice.taxAmount || 0) + 
                                      ((invoice.subtotal || 0) + (invoice.taxAmount || 0)) * ((companySettings.platformFee || 0) / 100)
                                    )
                                  : formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))
                                }
                              </span>
                            </div>
                          </div>
                          
                          <button 
                            className="w-full py-4 rounded-xl font-bold text-white transition-all duration-300 hover:opacity-90 hover:shadow-lg transform hover:-translate-y-0.5"
                            style={{ backgroundColor: colors.buttonBackground || '#8B5CF6' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (savedPaymentMethod) {
                                setUseNewPaymentMethod(true);
                              }
                              setIsPaymentModalOpen(true);
                            }}
                          >
                            Pay with Card
                          </button>
                        </div>

                        {/* Hover effect gradient */}
                        <div className="absolute inset-0 bg-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
                      </div>
                    </div>

                    {/* Security and Trust Indicators */}
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center space-x-6 text-xs text-gray-500 bg-gray-50 px-6 py-3 rounded-full">
                        <div className="flex items-center space-x-1">
                          <Shield className="w-3 h-3" />
                          <span>SSL Secured</span>
                        </div>
                        <div className="w-px h-3 bg-gray-300"></div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>Instant Processing</span>
                        </div>
                        <div className="w-px h-3 bg-gray-300"></div>
                        <span>PCI Compliant</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Watermarks */}
            {(invoiceSettings.showPaidWatermark || invoice.status === 'paid') && invoice.status === 'paid' && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className={cn(
                  'transform rotate-45 text-9xl font-bold',
                  styles.paidWatermark
                )}>
                  PAID
                </div>
              </div>
            )}
            {invoiceSettings.showUnpaidWatermark && invoice.status === 'overdue' && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className={cn(
                  'transform rotate-45 text-9xl font-bold',
                  styles.overdueWatermark
                )}>
                  PAST DUE
                </div>
              </div>
            )}

            {/* Payment Modal */}
            {(!savedPaymentMethod || useNewPaymentMethod) && (
              <PaymentModal
                open={isPaymentModalOpen}
                onOpenChange={setIsPaymentModalOpen}
                invoice={invoice as Invoice}
                invoiceAmount={(invoice.subtotal || 0) + (invoice.taxAmount || 0)}
                platformFee={companySettings?.platformFee || 0}
                enablePlatformFee={companySettings?.invoiceSettings?.enablePlatformFee || false}
                onPaymentSuccess={handlePaymentSuccess}
              />
            )}

            {/* ACH Payment Modal */}
            {invoice.id && (
              <ACHPaymentModal
                open={isACHPaymentModalOpen}
                onOpenChange={setIsACHPaymentModalOpen}
                invoice={{ ...invoice, id: invoice.id }}
                invoiceAmount={(invoice.subtotal || 0) + (invoice.taxAmount || 0)}
                onPaymentSuccess={handlePaymentSuccess}
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

// Add to the CSS in mobileStyles
MobileStyles.displayName = 'MobileStyles'; 
